import { PayGatewayOptions, PayloftGatewayMessage } from "../types";

/**
 * Validates initialization options and guards against common security pitfalls (e.g. passing Secret Keys).
 */
export function validateGatewayOptions(options: PayGatewayOptions): void {
  if (!options) {
    throw new Error("[Payloft Security] Missing PayGatewayOptions.");
  }

  if (!options.reference || typeof options.reference !== "string" || !options.reference.trim()) {
    throw new Error("[Payloft Security] A valid, non-empty transaction reference string is required.");
  }

  // Secret Key Guard: Check if developer mistakenly exposed a secret key
  const anyOptions = options as any;
  if (
    anyOptions.secretKey ||
    anyOptions.secret_key ||
    anyOptions.sk ||
    (typeof options.publicKey === "string" && options.publicKey.startsWith("sk_"))
  ) {
    console.error(
      "%c[Payloft Security Violation]%c NEVER expose your SECRET KEY on the client-side! Transactions must be initialized on your secure backend server. Only pass public references to @payloft/pay-react.",
      "color: red; font-weight: bold;",
      "color: inherit;"
    );
  }

  // Validate Gateway URL protocol
  if (options.gatewayUrl) {
    try {
      const url = new URL(options.gatewayUrl);
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        throw new Error(`[Payloft Security] Invalid gatewayUrl protocol: ${url.protocol}`);
      }
    } catch (err: any) {
      throw new Error(`[Payloft Security] Invalid gatewayUrl format: ${options.gatewayUrl}`);
    }
  }
}

/**
 * Validates whether an incoming postMessage is authentic, safe, and originated from the expected iframe instance.
 */
export function isTrustedMessage(
  event: MessageEvent,
  iframeWindow: Window | null,
  expectedGatewayOrigin: string
): boolean {
  // 1. Source Window Check: Must strictly match the checkout iframe's contentWindow
  if (iframeWindow && event.source !== iframeWindow) {
    return false;
  }

  // 2. Origin Verification: Origin must match the expected gateway origin
  if (expectedGatewayOrigin !== "*") {
    try {
      const expectedOrigin = new URL(expectedGatewayOrigin).origin;
      if (event.origin !== expectedOrigin) {
        return false;
      }
    } catch (e) {
      if (event.origin !== expectedGatewayOrigin) {
        return false;
      }
    }
  }

  // 3. Message Schema & Source Identifier Check
  const data = event.data as PayloftGatewayMessage;
  if (!data || typeof data !== "object") {
    return false;
  }

  if (data.source && data.source !== "payloft-gateway") {
    return false;
  }

  return true;
}
